import { Controller } from "@hotwired/stimulus";

/**
 * Swaps the title bar's title for the screen's own heading once that heading has scrolled under
 * it.
 *
 * `UINavigationBar` does this for a large title: the heading belongs to the screen while you can
 * see it, and moves into the bar when you cannot, so the bar always names what is actually under
 * it. The event screen is the case this exists for -- its bar says "Event" and its heading says
 * which one -- but the controller reads both ends from the markup and knows nothing about events.
 *
 * Both ends live in different subtrees: the bar is a direct child of `<body>` and the heading is
 * inside the scrolling page, so this is the one controller that has to sit on the body to have
 * both in reach.
 *
 * A screen that names no replacement, or has no heading to watch, gets a controller that connects
 * and does nothing. That is most of them, which is why the bar's markup carries no state of its
 * own: the swapped title is an attribute on the title the server already rendered.
 */
export default class extends Controller<HTMLElement> {
  static override targets = ["heading", "title"];

  declare readonly hasHeadingTarget: boolean;
  declare readonly hasTitleTarget: boolean;
  declare readonly headingTarget: HTMLElement;
  declare readonly titleTarget: HTMLElement;

  /** The bar's own title, kept so the swap can be undone on the way back up. */
  private pageTitle = "";

  private observer: IntersectionObserver | undefined;

  override connect(): void {
    if (!this.hasHeadingTarget || !this.hasTitleTarget) {
      return;
    }

    const scrolled = this.titleTarget.dataset["wpTitleScrolled"];

    if (scrolled === undefined || scrolled === "") {
      return;
    }

    /* Not `IntersectionObserver` in happy-dom, and not in a browser old enough to matter either.
           Without it the bar keeps the title the server rendered, which is the behaviour this
           replaces rather than a broken version of it. */
    if (typeof IntersectionObserver !== "function") {
      return;
    }

    this.pageTitle = this.titleTarget.textContent ?? "";

    /* The heading counts as gone when it passes under the bar rather than when it leaves the
           window, so the viewport is shortened by the bar's own height. Read from the custom
           property so the two cannot drift; happy-dom resolves a custom property to the empty
           string, which is what the fallback is for. */
    const height = Number.parseInt(
      getComputedStyle(document.documentElement).getPropertyValue(
        "--wp-navbar-height",
      ),
      10,
    );

    this.observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          this.render(entry.isIntersecting ? this.pageTitle : scrolled);
        }
      },
      {
        rootMargin: `-${Number.isNaN(height) ? 56 : height}px 0px 0px 0px`,
        threshold: 0,
      },
    );

    this.observer.observe(this.headingTarget);
  }

  override disconnect(): void {
    /* The observer outlives the element it was watching, so it goes with the controller rather
           than being left holding a node that has been navigated away from. */
    this.observer?.disconnect();
    this.observer = undefined;
  }

  /** Writes a title only when it is not the one already there, so a scroll does not churn the DOM. */
  private render(title: string): void {
    if (this.titleTarget.textContent !== title) {
      this.titleTarget.textContent = title;
    }
  }
}
